import FetchService from "@core-ui/ApiServices/FetchService";
import type FetchResponse from "@core-ui/ApiServices/Types/FetchResponse";
import Url from "@core-ui/ApiServices/Types/Url";
import PageDataContextService from "@core-ui/ContextServices/PageDataContext";
import ErrorConfirmService from "@ext/errorHandlers/client/ErrorConfirmService";
import DefaultError from "@ext/errorHandlers/logic/DefaultError";
import t from "@ext/localization/locale/translate";
import { useMemo } from "react";
import type { ReloadResult, ToolsTreeNode, ToolsType, WorkspaceToolsState } from "../../model/types";

export const DISABLED_STATE: WorkspaceToolsState = { enabled: false };

export type ApiResult<T> = { ok: true; value: T } | { ok: false };

type CallOptions = {
	query?: Record<string, string>;
	notify: boolean;
};

const networkError = (cause: unknown) =>
	new DefaultError(
		t("network.error.body"),
		cause instanceof Error ? cause : undefined,
		null,
		true,
		t("network.error.title"),
		"wifi-off",
	);

const unreadableAnswer = (cause: unknown) =>
	new DefaultError(
		t("app.error.command-failed.body"),
		cause instanceof Error ? cause : undefined,
		{ html: true },
		false,
		t("app.error.command-failed.title"),
	);

const call = async <T>(basePath: string, command: string, { query, notify }: CallOptions): Promise<ApiResult<T>> => {
	const url = Url.fromBasePath(`/api/workspaceTools/${command}`, basePath, query);
	let response: FetchResponse<T>;
	try {
		response = await FetchService.fetch<T>(url, undefined, undefined, undefined, notify);
	} catch (error) {
		if (notify) ErrorConfirmService.notify(networkError(error));
		return { ok: false };
	}
	if (!response.ok) return { ok: false };

	try {
		return { ok: true, value: await response.json() };
	} catch (error) {
		if (notify) ErrorConfirmService.notify(unreadableAnswer(error));
		return { ok: false };
	}
};

export const createWorkspaceToolsApi = (basePath: string) => ({
	ensure: (quiet = false) => call<WorkspaceToolsState>(basePath, "ensure", { notify: !quiet }),
	getState: () => call<WorkspaceToolsState>(basePath, "getState", { notify: false }),
	getTree: (type: ToolsType) => call<ToolsTreeNode[]>(basePath, "getTree", { query: { type }, notify: false }),
	getLink: (type: ToolsType, id: string) =>
		call<{ pathname: string } | null>(basePath, "getLink", { query: { type, id }, notify: true }),
	reload: (confirmed?: string) =>
		call<ReloadResult>(basePath, "reload", { query: confirmed ? { confirmed } : undefined, notify: true }),
});

export type WorkspaceToolsApi = ReturnType<typeof createWorkspaceToolsApi>;

export const useWorkspaceToolsApi = (): WorkspaceToolsApi => {
	const basePath = PageDataContextService.value.conf.basePath;
	return useMemo(() => createWorkspaceToolsApi(basePath), [basePath]);
};
